import type { fabric } from 'fabric';
import { isPixelGradient, resizeLinearGradient } from 'shared/fabric';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import type { FabricEditor } from '../core/fabric-editor';
import { FabricPluginName } from './plugin-name';

const EPS = 1e-6;

export function resizeNormalizePlugin(): EditorPlugin {
  return {
    name: FabricPluginName.RESIZE_NORMALIZE,
    install(editor: FabricEditor) {
      const off = editor.on('object:modified', ({ target }) => {
        if (!target || !isNormalizableRect(target)) return;
        const sx = target.scaleX ?? 1;
        const sy = target.scaleY ?? 1;
        if (Math.abs(sx - 1) < EPS && Math.abs(sy - 1) < EPS) return;
        // Capture the current visual center BEFORE baking. Rects use
        // originX/originY = 'left'/'top', so changing width/height pins the
        // object to its top-left origin corner. For a rotated object that
        // corner-pinning shifts the visible position sideways (the baked size
        // equals the scaled visual size, only the anchor differs). Re-pin to
        // the captured center afterwards so the object stays exactly where the
        // user left it, at any angle.
        const center = target.getCenterPoint();
        const from = { width: target.width ?? 0, height: target.height ?? 0 };
        const to = { width: from.width * sx, height: from.height * sy };
        target.set({ ...to, scaleX: 1, scaleY: 1 });
        // A pixel gradient is in object coordinates: with the scale baked away it
        // would cover only the old box. Rebuild it for the new size here, in the
        // same handler (no second emit). A percentage gradient stretches by
        // itself and stays untouched — an untouched gradient is never rebuilt.
        if (isPixelGradient(target.fill)) {
          target.set('fill', resizeLinearGradient(target.fill, from, to));
          target.dirty = true;
        }
        target.setPositionByOrigin(center, 'center', 'center');
        target.setCoords();
        editor.canvas.requestRenderAll();
      });
      (editor as unknown as { __resizeNormalizeOff?: () => void }).__resizeNormalizeOff = off;
    },
    dispose(editor: FabricEditor) {
      const slot = editor as unknown as { __resizeNormalizeOff?: () => void };
      slot.__resizeNormalizeOff?.();
      slot.__resizeNormalizeOff = undefined;
    },
  };
}

function isNormalizableRect(obj: fabric.Object): obj is fabric.Rect {
  return obj.type === 'rect';
}
